import React, { useEffect, useState } from "react";
import { 
  FaUsers, 
  FaPhoneAlt, 
  FaChalkboardTeacher, 
  FaBook, 
  FaCalendarAlt,
  FaUserGraduate,
  FaSchool,
  FaChartLine,
  FaTable,
  FaUserTie,
  FaCog,
  FaClipboardCheck,
  FaMoneyBillWave
} from "react-icons/fa";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Chart, registerables } from 'chart.js';
import SummaryApi from '../common/index';
import { useNavigate } from 'react-router-dom';

Chart.register(...registerables);

const Dashboard = () => {
  // State for counts
  const [counts, setCounts] = useState({
    students: 0,
    instructors: 0,
    courses: 0,
    colleges: 0,
    revenue: 0,
    newRegistrations: 0,
  });

  // State for dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Theme handling based on localStorage
  useEffect(() => {
    const savedTheme = sessionStorage.getItem("admin-theme");
    const shouldBeDark = savedTheme === "dark";
    setIsDarkMode(shouldBeDark);
    
    // Apply theme to document for global CSS support
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Listen for theme changes from other components
  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = sessionStorage.getItem("admin-theme");
      const shouldBeDark = savedTheme === "dark";
      setIsDarkMode(shouldBeDark);
      
      // Apply theme to document
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    window.addEventListener("themeChanged", handleThemeChange);
    
    // Listen for storage changes (other tabs)
    const handleStorageChange = (e) => {
      if (e.key === "admin-theme") {
        handleThemeChange();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom theme toggle events
    const handleCustomThemeChange = () => {
      handleThemeChange();
    };
    
    window.addEventListener("adminThemeToggled", handleCustomThemeChange);
    
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adminThemeToggled", handleCustomThemeChange);
    };
  }, []);

  // Chart colors based on theme
  const chartColors = {
    light: {
      revenue: {
        backgroundColor: 'rgba(255, 164, 4, 0.5)',
        borderColor: 'rgba(255, 164, 4, 1)'
      },
      performance: {
        backgroundColor: 'rgba(0, 82, 147, 0.5)',
        borderColor: 'rgba(0, 82, 147, 1)'
      },
      userDistribution: [
        'rgba(255, 164, 4, 0.7)',
        'rgba(0, 82, 147, 0.7)',
        'rgba(0, 51, 102, 0.7)'
      ],
      textColor: '#111827',
      gridColor: 'rgba(0, 0, 0, 0.1)'
    },
    dark: {
      revenue: {
        backgroundColor: 'rgba(255, 193, 7, 0.5)',
        borderColor: 'rgba(255, 193, 7, 1)'
      },
      performance: {
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)'
      },
      userDistribution: [
        'rgba(255, 193, 7, 0.7)',
        'rgba(59, 130, 246, 0.7)',
        'rgba(107, 114, 128, 0.7)'
      ],
      textColor: '#f9fafb',
      gridColor: 'rgba(255, 255, 255, 0.1)'
    }
  };

  const currentColors = isDarkMode ? chartColors.dark : chartColors.light;

  // State for charts data
  const [revenueData, setRevenueData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Monthly Revenue ($)',
      data: [3200, 4500, 3800, 5200, 6100, 5800],
      backgroundColor: currentColors.revenue.backgroundColor,
      borderColor: currentColors.revenue.borderColor,
      borderWidth: 2
    }]
  });

  const [performanceData, setPerformanceData] = useState({
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [{
      label: 'Performance Index',
      data: [78, 85, 82, 90],
      backgroundColor: currentColors.performance.backgroundColor,
      borderColor: currentColors.performance.borderColor,
      borderWidth: 2,
      tension: 0.3
    }]
  });

  const [userDistribution, setUserDistribution] = useState({
    labels: ['Students', 'Instructors', 'Colleges'],
    datasets: [{
      data: [0, 0, 0],
      backgroundColor: currentColors.userDistribution,
      borderWidth: 1
    }]
  });

  // State for instructor schedule
  const [instructorSchedule, setInstructorSchedule] = useState([]);

  // State for today enrollments
  const [todayEnrollments, setTodayEnrollments] = useState([]);

  // State for pagination
  const [currentPageActivities, setCurrentPageActivities] = useState(1);
  const [currentPageSchedule, setCurrentPageSchedule] = useState(1);
  const itemsPerPage = 5;

  // Update chart colors when theme changes
  useEffect(() => {
    setRevenueData(prev => ({
      ...prev,
      datasets: [{
        ...prev.datasets[0],
        backgroundColor: currentColors.revenue.backgroundColor,
        borderColor: currentColors.revenue.borderColor
      }]
    }));

    setPerformanceData(prev => ({
      ...prev,
      datasets: [{
        ...prev.datasets[0],
        backgroundColor: currentColors.performance.backgroundColor,
        borderColor: currentColors.performance.borderColor
      }]
    }));

    setUserDistribution(prev => ({
      ...prev,
      datasets: [{
        ...prev.datasets[0],
        backgroundColor: currentColors.userDistribution
      }]
    }));
  }, [isDarkMode, currentColors]);

  // Function to format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return `${interval} years ago`;
    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return `${interval} months ago`;
    interval = Math.floor(seconds / 86400);
    if (interval > 1) return `${interval} days ago`;
    if (interval === 1) return `1 day ago`;
    interval = Math.floor(seconds / 3600);
    if (interval > 1) return `${interval} hours ago`;
    if (interval === 1) return `1 hour ago`;
    interval = Math.floor(seconds / 60);
    if (interval > 1) return `${interval} minutes ago`;
    if (interval === 1) return `1 minute ago`;
    return `just now`;
  };

  // Fetch dashboard counts
  const fetchDashboardCounts = async () => {
    try {
      const response = await fetch(SummaryApi.AdminDashboardCounts.url, {
        method: SummaryApi.AdminDashboardCounts.method,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status) {
        setCounts({
          students: result.data.total_students,
          instructors: result.data.total_instructors,
          courses: result.data.total_courses,
          colleges: result.data.total_colleges,
          revenue: 12500,
          newRegistrations: result.data.this_week_students
        });
        setUserDistribution({
          labels: ['Students', 'Instructors', 'Colleges'],
          datasets: [{
            data: [result.data.total_students, result.data.total_instructors, result.data.total_colleges],
            backgroundColor: currentColors.userDistribution,
            borderWidth: 1
          }]
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard counts:', error);
    }
  };

  // Fetch instructor schedule
  const fetchInstructorSchedule = async () => {
    try {
      const scheduleResponse = await fetch(SummaryApi.instructorregistrationstats.url, {
        method: SummaryApi.instructorregistrationstats.method,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const scheduleResult = await scheduleResponse.json();
      if (scheduleResult.status) {
        setInstructorSchedule(scheduleResult.data || []);
      }
    } catch (error) {
      console.error('Error fetching instructor schedule:', error);
    }
  };

  // Fetch today student enrollments
  const fetchTodayEnrollments = async () => {
    try {
      const response = await fetch(SummaryApi.today_student_enrollments.url, {
        method: SummaryApi.today_student_enrollments.method,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status) {
        setTodayEnrollments((result.data || []).map(enrollment => ({
          id: enrollment.enrollment_id,
          action: 'Student enrollment',
          user: `Student ${enrollment.student_id} in Course ${enrollment.course_id}`,
          time: formatTimeAgo(new Date(enrollment.enrollment_date))
        })));
      }
    } catch (error) {
      console.error('Error fetching today enrollments:', error);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardCounts();
    fetchInstructorSchedule();
    fetchTodayEnrollments();
  }, []);
useEffect(() => {
  const interval = setInterval(() => {
    fetchInstructorSchedule();
    fetchTodayEnrollments();
  }, 60000); // 1 minute

  return () => clearInterval(interval);
}, [])
  // Pagination logic for Today Enrollments
  const indexOfLastActivity = currentPageActivities * itemsPerPage;
  const indexOfFirstActivity = indexOfLastActivity - itemsPerPage;
  const currentActivities = todayEnrollments.slice(indexOfFirstActivity, indexOfLastActivity);
  const totalPagesActivities = Math.ceil(todayEnrollments.length / itemsPerPage);

  // Pagination logic for Instructor Schedule
  const indexOfLastSchedule = currentPageSchedule * itemsPerPage;
  const indexOfFirstSchedule = indexOfLastSchedule - itemsPerPage;
  const currentSchedule = instructorSchedule.slice(indexOfFirstSchedule, indexOfLastSchedule);
  const totalPagesSchedule = Math.ceil(instructorSchedule.length / itemsPerPage);

  // Handle page change
  const handlePageChangeActivities = (pageNumber) => {
    setCurrentPageActivities(pageNumber);
  };

  const handlePageChangeSchedule = (pageNumber) => {
    setCurrentPageSchedule(pageNumber);
  };

  const handlePrevActivities = () => {
    if (currentPageActivities > 1) {
      setCurrentPageActivities(currentPageActivities - 1);
    }
  };

  const handleNextActivities = () => {
    if (currentPageActivities < totalPagesActivities) {
      setCurrentPageActivities(currentPageActivities + 1);
    }
  };

  const handlePrevSchedule = () => {
    if (currentPageSchedule > 1) {
      setCurrentPageSchedule(currentPageSchedule - 1);
    }
  };

  const handleNextSchedule = () => {
    if (currentPageSchedule < totalPagesSchedule) {
      setCurrentPageSchedule(currentPageSchedule + 1);
    }
  };

  const navigate = useNavigate();

  // Chart options with theme support
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          color: currentColors.textColor
        },
        grid: {
          color: currentColors.gridColor
        }
      },
      y: {
        ticks: {
          color: currentColors.textColor
        },
        grid: {
          color: currentColors.gridColor
        }
      }
    },
    plugins: {
      legend: {
        labels: {
          color: currentColors.textColor
        }
      }
    },
    animation: {
      duration: 2000
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        beginAtZero: true,
        ticks: {
          ...chartOptions.scales.y.ticks,
          callback: function(value) {
            return '$' + value;
          }
        }
      }
    }
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        beginAtZero: true,
        max: 100,
        ticks: {
          ...chartOptions.scales.y.ticks,
          callback: function(value) {
            return value + '%';
          }
        }
      }
    }
  };

  const pieChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        position: 'right',
        labels: {
          color: currentColors.textColor
        }
      }
    }
  };

  return (
    <div className={`p-4 md:p-6 lg:p-8 min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      <h2 className={`text-3xl font-bold text-center mb-8 animate-fade-in ${
        isDarkMode ? 'text-white' : 'text-[#001529]'
      }`}>
        Admin Dashboard
      </h2>
      
      {/* Stats Widgets - Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Students Widget */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200'
          }`}
          style={{ animationDelay: '0.1s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-yellow-500 bg-opacity-10 border-yellow-500 border-opacity-30' 
                : 'bg-orange-100 border-orange-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(255, 164, 4, 0.2)'
            }}
          >
            <FaUserGraduate className={isDarkMode ? "text-yellow-400" : "text-orange-500"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Total Students</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>{counts.students}</p>
          </div>
        </div>

        {/* Instructors Widget */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
          }`}
          style={{ animationDelay: '0.2s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-blue-500 bg-opacity-10 border-blue-500 border-opacity-30' 
                : 'bg-blue-100 border-blue-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
            }}
          >
            <FaChalkboardTeacher className={isDarkMode ? "text-blue-400" : "text-blue-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Instructors</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>{counts.instructors}</p>
          </div>
        </div>

        {/* Courses Widget */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
          }`}
          style={{ animationDelay: '0.3s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-green-500 bg-opacity-10 border-green-500 border-opacity-30' 
                : 'bg-green-100 border-green-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(34, 197, 94, 0.2)'
            }}
          >
            <FaBook className={isDarkMode ? "text-green-400" : "text-green-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Courses</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>{counts.courses}</p>
          </div>
        </div>

        {/* Revenue Widget */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'
          }`}
          style={{ animationDelay: '0.4s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-red-500 bg-opacity-10 border-red-500 border-opacity-30' 
                : 'bg-red-100 border-red-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
            }}
          >
            <FaMoneyBillWave className={isDarkMode ? "text-red-400" : "text-red-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Revenue</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>₹{counts.revenue}</p>
            <p className={`text-[10px] mt-1 flex items-center ${
              isDarkMode ? 'text-green-400' : 'text-green-600'
            }`}>
              <span className="inline-block mr-1">↑</span>+18% this month
            </p>
          </div>
        </div>
      </div>

      {/* Stats Widgets - Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* New Registrations */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200'
          }`}
          style={{ animationDelay: '0.1s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-purple-500 bg-opacity-10 border-purple-500 border-opacity-30' 
                : 'bg-purple-100 border-purple-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(147, 51, 234, 0.2)'
            }}
          >
            <FaUsers className={isDarkMode ? "text-purple-400" : "text-purple-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>New Registrations</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>{counts.newRegistrations}</p>
            <p className={`text-[10px] mt-1 ${
              isDarkMode ? 'text-blue-400' : 'text-blue-600'
            }`}>This week</p>
          </div>
        </div>

        {/* Colleges Widget */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'
          }`}
          style={{ animationDelay: '0.2s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-gray-500 bg-opacity-10 border-gray-500 border-opacity-30' 
                : 'bg-gray-100 border-gray-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(107, 114, 128, 0.2)'
            }}
          >
            <FaSchool className={isDarkMode ? "text-gray-400" : "text-gray-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Total Colleges</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>{counts.colleges}</p>
          </div>
        </div>

        {/* Upcoming Events */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
          }`}
          style={{ animationDelay: '0.3s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-amber-500 bg-opacity-10 border-amber-500 border-opacity-30' 
                : 'bg-amber-100 border-amber-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
            }}
          >
            <FaCalendarAlt className={isDarkMode ? "text-amber-400" : "text-amber-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Total Student Completed Courses</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>80</p>
            <p className={`text-[10px] mt-1 flex items-center ${
              isDarkMode ? 'text-green-400' : 'text-green-600'
            }`}>
              <span className="inline-block mr-1">✓</span>Total
            </p>
          </div>
        </div>

        {/* System Health */}
        <div 
          className={`rounded-xl shadow-lg p-6 flex items-center transition-all duration-300 hover:shadow-xl hover:scale-[1.02] animate-fade-in ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200'
          }`}
          style={{ animationDelay: '0.4s' }}
        >
          <div 
            className={`p-3 rounded-xl flex-shrink-0 ${
              isDarkMode 
                ? 'bg-emerald-500 bg-opacity-10 border-emerald-500 border-opacity-30' 
                : 'bg-emerald-100 border-emerald-300'
            }`}
            style={{
              marginRight: '1.5rem',
              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
            }}
          >
            <FaCog className={isDarkMode ? "text-emerald-400" : "text-emerald-600"} style={{ fontSize: '1.35rem' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-medium truncate ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>Student Completed Course</h3>
            <p className={`text-2xl font-bold mt-1 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>40</p>
            <p className={`text-[10px] mt-1 flex items-center ${
              isDarkMode ? 'text-green-400' : 'text-green-600'
            }`}>
              <span className="inline-block mr-1">✓</span>This month
            </p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className={`rounded-xl shadow-lg p-6 animate-slide-up ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>Revenue Overview</h3>
          <div className="h-64">
            <Bar 
              data={revenueData} 
              options={barChartOptions}
            />
          </div>
        </div>

        {/* Performance Chart */}
        <div className={`rounded-xl shadow-lg p-6 animate-slide-up ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>Quarterly Performance</h3>
          <div className="h-64">
            <Line 
              data={performanceData} 
              options={lineChartOptions}
            />
          </div>
        </div>

        {/* User Distribution */}
        <div className={`rounded-xl shadow-lg p-6 animate-slide-up ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>User Distribution</h3>
          <div className="h-64">
            <Pie 
              data={userDistribution} 
              options={pieChartOptions}
            />
          </div>
        </div>
      </div>

      {/* Table and Instructor Schedule Row */}


  {/* Recent Activities Table */}
  <div className={`shadow-md mb-4 rounded-lg overflow-hidden animate-slide-up ${
    isDarkMode ? 'bg-gray-800' : 'bg-white'
  }`}>
    <h3 className={`text-xl font-semibold p-4 border-b ${
      isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-800 border-gray-200'
    }`}>Recent Student Enrollments</h3>
    <div className="max-h-80 overflow-y-auto">
      <table className="w-full table-auto">
        <thead className={`sticky top-0 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <tr>
            <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider w-1/12 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>S.No</th>
            <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider w-2/12 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>Student name</th>
                   <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider w-2/12 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>email</th>
            <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider w-2/12 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>course name</th>
            <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider w-2/12 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>Enrolled date</th>
          </tr>
        </thead>
        <tbody className={`divide-y ${
          isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
        }`}>
          {currentActivities.length > 0 ? (
            currentActivities.map((activity, index) => (
              <tr key={activity.id} className={`transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}>
                <td className={`px-4 py-3 text-base font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>{indexOfFirstActivity + index + 1}</td>
                <td className={`px-4 py-3 text-base ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{activity.action}</td>
     


                <td className={`px-4 py-3 text-base ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{activity.user}</td>
                  <td className={`px-4 py-3 text-base ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{activity.email}</td>


                <td className={`px-4 py-3 text-base ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{activity.time}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className={`px-4 py-3 text-base text-center ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>No records found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    {/* Pagination */}
    <div className={`flex justify-between items-center p-4 border-t ${
      isDarkMode ? 'border-gray-700' : 'border-gray-200'
    }`}>
      <span className={`text-base ${
        isDarkMode ? 'text-gray-300' : 'text-gray-700'
      }`}>
        Showing {todayEnrollments.length > 0 ? indexOfFirstActivity + 1 : 0} to {Math.min(indexOfLastActivity, todayEnrollments.length)} of {todayEnrollments.length}
      </span>
      <div className="flex space-x-2">
        <button
          onClick={handlePrevActivities}
          disabled={currentPageActivities === 1}
          className={`px-4 py-1 rounded-md text-sm font-medium transition-colors ${
            isDarkMode 
              ? 'bg-blue-700 text-gray-300 hover:bg-blue-600 disabled:opacity-50' 
              : 'bg-blue-100 text-gray-700 hover:bg-blue-200 disabled:opacity-50'
          }`}
        >
          Prev
        </button>
        <button
          onClick={handleNextActivities}
          disabled={currentPageActivities === totalPagesActivities}
          className={`px-4 py-1 rounded-md text-sm font-medium transition-colors ${
            isDarkMode 
              ? 'bg-blue-700 text-gray-300 hover:bg-blue-600 disabled:opacity-50' 
              : 'bg-blue-100 text-gray-700 hover:bg-blue-200 disabled:opacity-50'
          }`}
        >
          Next
        </button>
      </div>
    </div>
  </div>



  {/* Instructor Schedule Table - Updated with responsive classes */}
  <div className={`shadow-md mt-2 rounded-lg overflow-hidden animate-slide-up ${
    isDarkMode ? 'bg-gray-800' : 'bg-white'
  }`}>
    <h3 className={`text-xl font-semibold p-4 border-b ${
      isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-800 border-gray-200'
    }`}>Instructor Registrations</h3>
    
    {/* Responsive container - no max-height on large screens */}
    <div className="lg:overflow-visible overflow-x-auto">
      <div className="min-w-full lg:min-w-0">
        <table className="w-full table-auto">
          <thead className={`${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
            <tr>
              <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>S.No</th>
              <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Instructor</th>
              <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Mobile</th>
              <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Email</th>
              <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Experience</th>
              <th className={`px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Action</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${
            isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
          }`}>
            {currentSchedule.length > 0 ? (
              currentSchedule.map((schedule, index) => (
                <tr key={index} className={`transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}>
                  <td className={`px-4 py-3 text-base font-medium ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>{indexOfFirstSchedule + index + 1}</td>
                  <td className={`px-4 py-3 text-base ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{`${schedule.first_name} ${schedule.last_name}` || 'N/A'}</td>
                  <td className={`px-4 py-3 text-base ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{schedule.mobile_number || 'N/A'}</td>
                  <td className={`px-4 py-3 text-base ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{schedule.email || 'N/A'}</td>
                  <td className={`px-4 py-3 text-base ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{schedule.total_experience || 'N/A'}</td>
                  <td className="px-4 py-3 text-base">
                    <button 
                      onClick={() => window.open(`/admin-panel/create-instructor?id=${btoa(schedule.instructor_id)}`, '_blank')}
                      className="px-4 py-1 bg-[#1C3664] text-white rounded hover:bg-[#1A73E8] transition-colors text-sm font-medium"
                    >
                      Approve
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className={`px-4 py-3 text-base text-center ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>No records found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    
    {/* Pagination */}
    <div className={`flex justify-between items-center p-4 border-t ${
      isDarkMode ? 'border-gray-700' : 'border-gray-200'
    }`}>
      <span className={`text-base ${
        isDarkMode ? 'text-gray-300' : 'text-gray-700'
      }`}>
        Showing {instructorSchedule.length > 0 ? indexOfFirstSchedule + 1 : 0} to {Math.min(indexOfLastSchedule, instructorSchedule.length)} of {instructorSchedule.length}
      </span>
      <div className="flex space-x-2">
        <button
          onClick={handlePrevSchedule}
          disabled={currentPageSchedule === 1}
          className={`px-4 py-1 rounded-md text-sm font-medium transition-colors ${
            isDarkMode 
              ? 'bg-blue-700 text-gray-300 hover:bg-blue-600 disabled:opacity-50' 
              : 'bg-blue-100 text-gray-700 hover:bg-blue-200 disabled:opacity-50'
          }`}
        >
          Prev
        </button>
        <button
          onClick={handleNextSchedule}
          disabled={currentPageSchedule === totalPagesSchedule}
          className={`px-4 py-1 rounded-md text-sm font-medium transition-colors ${
            isDarkMode 
              ? 'bg-blue-700 text-gray-300 hover:bg-blue-600 disabled:opacity-50' 
              : 'bg-blue-100 text-gray-700 hover:bg-blue-200 disabled:opacity-50'
          }`}
        >
          Next
        </button>
      </div>
    </div>
  </div>
      {/* Custom Animations */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;